import React from "react";

export const OPS_PANEL = "border border-[rgba(120,170,255,0.12)] bg-[#0a0f1d]";
export const OPS_BORDER = "border-[rgba(120,170,255,0.12)]";
export const OPS_INPUT = "h-9 rounded-none border-[rgba(120,170,255,0.14)] bg-[#050a16] text-sm focus-visible:ring-1 focus-visible:ring-sky-400";

export const OpsPage = ({ icon: Icon, eyebrow, title, actions, children, bodyClass = "" }) => (
  <div className="min-w-0">
    <div className={`sticky top-14 z-30 flex h-12 items-center gap-3 border-b ${OPS_BORDER} bg-[#040714]/95 px-3 backdrop-blur-sm sm:px-4`}>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-[#4d8bff]" />}
      <div className="flex min-w-0 items-baseline gap-3">
        <h1 className="display truncate text-xl font-black uppercase leading-none tracking-tight text-white">{title}</h1>
        {eyebrow && <span className="mono hidden shrink-0 text-[9px] uppercase tracking-[0.22em] text-slate-500 lg:block">{eyebrow}</span>}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
    </div>
    <div className={`p-3 sm:p-4 ${bodyClass}`}>{children}</div>
  </div>
);

export const Panel = ({ title, icon: Icon, note, actions, children, bodyClass = "p-4", className = "" }) => (
  <section className={`${OPS_PANEL} ${className}`}>
    {(title || actions) && (
      <header className={`flex h-9 items-center gap-2 border-b ${OPS_BORDER} px-3`}>
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-[#4d8bff]" />}
        {title && <h2 className="display text-sm font-bold uppercase tracking-wide text-white">{title}</h2>}
        {note && <span className="mono hidden truncate text-[9px] uppercase tracking-widest text-slate-600 md:block">{note}</span>}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</div>
      </header>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

const chipTone = {
  ok: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  blocked: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  error: "text-red-300 border-red-500/30 bg-red-500/10",
  live: "text-white border-red-500 bg-red-600",
  preview: "text-white border-sky-500 bg-sky-600",
  idle: "text-slate-400 border-white/10 bg-white/[0.03]",
};
const dotTone = {
  ok: "bg-emerald-400",
  blocked: "bg-amber-400",
  error: "bg-red-400",
  live: "bg-white",
  preview: "bg-white",
  idle: "bg-slate-600",
};

export const StatusChip = ({ state = "idle", dot = true, children, testId, className = "" }) => (
  <span data-testid={testId} className={`mono inline-flex items-center gap-1.5 border px-2 py-1 text-[9px] font-bold uppercase tracking-widest ${chipTone[state] || chipTone.idle} ${className}`}>
    {dot && <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotTone[state] || dotTone.idle}`} />}
    {children}
  </span>
);

export const EmptyState = ({ icon: Icon, title, message, action, testId }) => (
  <div data-testid={testId} className="grid min-h-52 place-items-center p-8 text-center">
    <div>
      {Icon && <Icon className="mx-auto h-8 w-8 text-slate-700" />}
      <div className="display mt-3 text-base font-bold uppercase text-slate-200">{title}</div>
      {message && <div className="mono mx-auto mt-2 max-w-sm text-[10px] uppercase leading-relaxed tracking-wider text-slate-600">{message}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  </div>
);

export const OpsTabs = ({ tabs, active, onChange }) => (
  <div className={`flex overflow-x-auto border ${OPS_BORDER} bg-[#0a0f1d] p-1`}>
    {tabs.map(({ key, label, icon: Icon }) => (
      <button
        key={key}
        type="button"
        data-testid={`ops-tab-${key}`}
        onClick={() => onChange(key)}
        aria-pressed={active === key}
        className={`flex h-8 shrink-0 items-center gap-2 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 ${active === key ? "bg-[#1a5cff] text-white" : "text-slate-500 hover:bg-white/[0.04] hover:text-white"}`}
      >
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </button>
    ))}
  </div>
);

export const Kbd = ({ children }) => (
  <kbd className="mono border border-white/15 bg-white/[0.06] px-1.5 py-0.5 text-[9px] font-bold leading-none text-slate-300">{children}</kbd>
);

export const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mono block text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">{children}</label>
);

export const SkeletonRows = ({ rows = 3 }) => (
  <div className="space-y-2 p-4">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="h-9 animate-pulse bg-white/[0.04]" />
    ))}
  </div>
);
